import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
    UiDialogRoot,
    UiDialogContent,
    UiDialogTitle,
    UiDialogDescription,
    UiDialogClose,
    UiDialogHeader,
    UiDialogBody,
    UiDialogFooter,
} from "../../../components/ui/ant/Dialog.jsx";
import {
  Button,
  Form,
  Input,
  Select,
  Switch,
  App
} from 'antd';
import "antd/dist/reset.css";

function Row({ label, children }) {
    return (
        <div className="flex flex-col gap-2 w-full">
            <div className="eyebrow">{label}</div>
            <div className="w-full">{children}</div>
        </div>
    );
}

Row.propTypes = {
    label: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};

export { Row as DialogEditStringsRow };

const defaultGetEntityId = (entity) => entity?.id;
const defaultGetInitialExtraState = () => ({});
const defaultBuildPayload = ({ name, description }) => ({
    name: name.trim(),
    description: description.trim() || null,
});

export default function DialogEditStrings({
    open,
    entity,
    projectId,
    versionId,
    onClose,
    onSaved,
    title,
    renameSystemName,
    patchEntity,
    getEntityId = defaultGetEntityId,
    saveErrorMessage = "Не удалось сохранить изменения",
    systemNamePlaceholder = "iron_ingot",
    namePlaceholder = "Iron Ingot",
    dialogSize = "md",
    getInitialExtraState = defaultGetInitialExtraState,
    buildPayload = defaultBuildPayload,
    renderExtraFields,
}) {
    const entityId = getEntityId(entity);
    const [systemName, setSystemName] = useState("");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [extraState, setExtraState] = useState(() => getInitialExtraState(entity));

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open || !entity) return;
        setSystemName(entity.systemName || "");
        setName(entity.name || "");
        setDescription(entity.description || "");
        setExtraState(getInitialExtraState(entity));
        setError("");
    }, [open, entity, getInitialExtraState]);

    const canSubmit = useMemo(
        () => !!entityId && systemName.trim() && name.trim() && !saving,
        [entityId, systemName, name, saving],
    );

    const onSubmit = async (e) => {
        e?.preventDefault?.();
        if (!canSubmit) return;

        setSaving(true);
        setError("");

        try {
            let current = entity;
            if (renameSystemName && systemName.trim() !== (entity.systemName || "")) {
                current = await renameSystemName(projectId, versionId, entityId, systemName.trim(), entity);
            }

            const payload = buildPayload({
                entity,
                entityId,
                systemName,
                name,
                description,
                extraState,
            });

            const updated = await patchEntity(projectId, versionId, entityId, payload, entity);

            onSaved?.(updated || current);
            onClose?.();
        } catch (e) {
            setError(e?.message || saveErrorMessage);
        } finally {
            setSaving(false);
        }
    };

    const setExtraField = (field, value) => {
        setExtraState((current) => ({
            ...current,
            [field]: value,
        }));
    };

    if (!open || !entity) return null;

    return (
        <App>
            <UiDialogRoot open={open} onOpenChange={(next) => !next && onClose?.()}>
                <UiDialogContent size={dialogSize}>
                    <UiDialogHeader>
                        <div>
                            <UiDialogTitle>{title}</UiDialogTitle>
                            <UiDialogDescription>ID: {entityId}</UiDialogDescription>
                        </div>
                        <UiDialogClose showIcon />
                    </UiDialogHeader>

                    <UiDialogBody>
                        {error && (
                            <div className="mb-4 rounded-xl border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
                                {error}
                            </div>
                        )}

                        <Form layout='vertical' className="space-y-4" onSubmit={onSubmit}>
                            <Form.Item label="systemName">
                                <Input
                                    className="input w-full"
                                    value={systemName}
                                    onChange={(e) => setSystemName(e.target.value)}
                                    placeholder={systemNamePlaceholder}
                                    required
                                />
                            </Form.Item>

                            <Form.Item label="name">
                                <Input
                                    className="input w-full"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder={namePlaceholder}
                                    required
                                />
                            </Form.Item>

                            <Form.Item label="description">
                                <Input.TextArea
                                    className="input w-full"
                                    rows={3}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Описание для UI"
                                />
                            </Form.Item>

                            {renderExtraFields?.({
                                entity,
                                entityId,
                                extraState,
                                setExtraField,
                                saving,
                            })}
                        </Form>
                    </UiDialogBody>

                    <UiDialogFooter>
                        <button type="button" className="btn btn-soft-accent" onClick={onSubmit} disabled={!canSubmit}>
                            {saving ? "Сохраняем..." : "Сохранить"}
                        </button>
                        <button type="button" className="btn btn-outline" onClick={onClose}>
                            Отмена
                        </button>
                    </UiDialogFooter>
                </UiDialogContent>
            </UiDialogRoot>
        </App>
    );
}


DialogEditStrings.propTypes = {
    open: PropTypes.bool,
    entity: PropTypes.object,
    projectId: PropTypes.string,
    versionId: PropTypes.string,
    onClose: PropTypes.func,
    onSaved: PropTypes.func,
    title: PropTypes.string.isRequired,
    renameSystemName: PropTypes.func,
    patchEntity: PropTypes.func.isRequired,
    getEntityId: PropTypes.func,
    saveErrorMessage: PropTypes.string,
    systemNamePlaceholder: PropTypes.string,
    namePlaceholder: PropTypes.string,
    dialogSize: PropTypes.string,
    getInitialExtraState: PropTypes.func,
    buildPayload: PropTypes.func,
    renderExtraFields: PropTypes.func,
};